import { DownOutlined } from '@ant-design/icons';
import { DrawerForm, PageContainer, ProTable } from '@ant-design/pro-components';
import { useMutation } from '@tanstack/react-query';
import { App, Button, Dropdown } from 'antd';
import { useRef, useState } from 'react';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import FactAnchorPreview from '../components/FactAnchorPreview';
import {
  getIpCreatorRecord,
  queryIpCreatorRecords,
  removeIpCreatorRecord,
} from '../services/ip-creator';

const resource = 'fact-anchors';
const resourceName = '材料搜集';

const seedTypeValueEnum = {
  hotPot: { text: '热点采集' },
  seedPlan: { text: '创作规划' },
};

const factSections = [
  ['backgroundFacts', '背景事实'],
  ['dataPoints', '数据指标'],
  ['cases', '案例材料'],
  ['concepts', '概念定义'],
  ['userSignals', '用户信号'],
  ['controversies', '争议分歧'],
  ['timelines', '时间线'],
];

const getFactAnchor = (record) => record?.factAnchor || {};

const renderSeedTitle = (_, record) => record.seedTitle || record.seedId || '-';

const renderFactCount = (_, record) =>
  factSections.reduce((total, [key]) => total + (getFactAnchor(record)[key]?.length || 0), 0);

const FactAnchors = () => {
  const actionRef = useRef(null);
  const [detailRecord, setDetailRecord] = useState(null);
  const { organizationId } = useOrganizationPath();
  const { message, modal } = App.useApp();

  const reload = () => actionRef.current?.reload?.();

  const { mutateAsync: removeRecord, isPending: removing } = useMutation({
    mutationFn: (payload) => removeIpCreatorRecord(resource, payload),
    onSuccess: () => {
      message.success(`${resourceName}已移除`);
      reload();
    },
    onError: () => message.error(`${resourceName}移除失败，请重试`),
  });

  const { mutateAsync: getRecord } = useMutation({
    mutationFn: (payload) => getIpCreatorRecord(resource, payload),
    onSuccess: (payload) => setDetailRecord(payload?.data || payload),
    onError: () => message.error(`${resourceName}详情读取失败，请重试`),
  });

  const removeRecordWithConfirm = (record) => {
    modal.confirm({
      title: `移除${resourceName}`,
      content: `确认移除 ${record.seedTitle || record.seedId}？`,
      okText: '确认',
      cancelText: '取消',
      onOk: () => removeRecord({ data: { id: record.id, organizationId } }),
    });
  };

  const handleMoreAction = (key, record) => {
    if (key === 'remove') {
      removeRecordWithConfirm(record);
    }
  };

  return (
    <PageContainer content="搜集多维度材料，供选题策划使用">
      <ProTable
        actionRef={actionRef}
        rowKey="id"
        headerTitle={resourceName}
        request={(params) => queryIpCreatorRecords(resource, { ...params, organizationId })}
        columns={[
          {
            title: '种子标题',
            dataIndex: 'seedTitle',
            ellipsis: true,
            fixed: 'left',
            render: renderSeedTitle,
            search: false,
            width: 220,
          },
          {
            title: '种子类型',
            dataIndex: 'seedType',
            order: 80,
            valueEnum: seedTypeValueEnum,
            valueType: 'select',
            width: 120,
          },
          {
            title: '材料数量',
            dataIndex: 'factCount',
            render: renderFactCount,
            search: false,
            width: 120,
          },
          { title: '创建时间', dataIndex: 'createdAt', valueType: 'dateTime', search: false },
          {
            title: '操作',
            valueType: 'option',
            render: (_, record) => [
              <Button
                key="detail"
                type="link"
                onClick={() => getRecord({ data: { id: record.id, organizationId } })}
              >
                详情
              </Button>,
              <Dropdown
                key="more"
                menu={{
                  onClick: ({ key }) => handleMoreAction(key, record),
                  items: [
                    {
                      key: 'remove',
                      danger: true,
                      disabled: removing,
                      label: '移除',
                    },
                  ],
                }}
              >
                <Button type="link">
                  更多 <DownOutlined />
                </Button>
              </Dropdown>,
            ],
          },
        ]}
        search={{ labelWidth: 96 }}
        scroll={{ x: 'max-content' }}
      />
      <DrawerForm
        title={`${resourceName}详情`}
        open={Boolean(detailRecord)}
        onOpenChange={(open) => {
          if (!open) {
            setDetailRecord(null);
          }
        }}
        submitter={false}
        width={1024}
      >
        <FactAnchorPreview record={detailRecord} />
      </DrawerForm>
    </PageContainer>
  );
};

export default FactAnchors;
